import React from 'react';
import { useSelector } from 'react-redux';
import StatisticsProvider from '../../StatisticsProvider';
import Metric from '../../../../components/stats/Metric';
import MetricsContainer from '../../../../components/stats/MetricsContainer';
import useHelpers from '../../../../hooks/context/useHelpers';
import useStatistics from '../../../../hooks/data/useStatistics';
import HelperMethods from '../../../../services/exports/HelperMethods';

function Metrics() {
  const { stats, isMetricLoading, getMetricLabel } = useStatistics();
  const { company } = useSelector((state) => state.currentCompany);
  const { franchise } = company;
  const isGiftCardsEnabled = franchise?.is_gift_cards_enabled;

  const { formatCurrency } = useHelpers();

  const moneyFormatter = (value) => formatCurrency(value);
  const percentageFormatter = (value) => HelperMethods.formatPercentage(value);

  const metrics = stats
    ? [
        <Metric
          key={getMetricLabel('sales')}
          name={getMetricLabel('sales')}
          loading={isMetricLoading(['sales', 'online_sales', 'cash_sales'])}
          mainMetrics={['sales', 'online_sales', 'cash_sales']}
          items={[
            {
              name: getMetricLabel('sales'),
              formatter: moneyFormatter,
              ...stats.sales,
            },
            {
              name: getMetricLabel('online_sales'),
              formatter: moneyFormatter,
              ...stats.online_sales,
            },
            {
              name: getMetricLabel('cash_sales'),
              formatter: moneyFormatter,
              ...stats.cash_sales,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('payouts')}
          name={getMetricLabel('payouts')}
          loading={isMetricLoading(['last_payout_amount', 'pending_payout_amount', 'future_payout_amount'])}
          excludedHistoryMetrics={['*']}
          mainMetrics={['last_payout_amount', 'pending_payout_amount', 'future_payout_amount']}
          items={[
            {
              name: getMetricLabel('last_payout_amount'),
              formatter: moneyFormatter,
              ...stats.last_payout_amount,
            },
            {
              name: getMetricLabel('pending_payout_amount'),
              formatter: moneyFormatter,
              ...stats.pending_payout_amount,
            },
            {
              name: getMetricLabel('future_payout_amount'),
              formatter: moneyFormatter,
              ...stats.future_payout_amount,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('savings')}
          name={getMetricLabel('savings')}
          items={stats.savings}
          formatter={moneyFormatter}
          loading={isMetricLoading(['savings'])}
          mainMetrics={['savings']}
        />,
        <Metric
          key={getMetricLabel('tips')}
          name={getMetricLabel('tips')}
          items={stats.tips}
          formatter={moneyFormatter}
          loading={isMetricLoading(['tips'])}
          mainMetrics={['tips']}
        />,
        <Metric
          key={getMetricLabel('average_order_value')}
          name={getMetricLabel('average_order_value')}
          items={stats.average_order_value}
          formatter={moneyFormatter}
          loading={isMetricLoading(['average_order_value'])}
          mainMetrics={['average_order_value']}
          excludedHistoryMetrics={['*']}
        />,
        <Metric
          key={getMetricLabel('average_open_rate')}
          name={getMetricLabel('average_open_rate')}
          items={stats.average_open_rate}
          formatter={(value) => HelperMethods.formatPercentage((value ?? 0) * 100)}
          loading={isMetricLoading(['average_open_rate'])}
          marketingMetrics={['average_open_rate']}
          excludedHistoryMetrics={['*']}
        />,
        isGiftCardsEnabled && (
          <Metric
            key={getMetricLabel('gift_cards_sales')}
            name={getMetricLabel('gift_cards_sales')}
            loading={isMetricLoading(['gift_cards_sales'])}
            mainMetrics={['gift_cards_sales']}
            items={[
              {
                name: getMetricLabel('gift_cards_sales'),
                formatter: moneyFormatter,
                ...stats.gift_cards_sales,
              },
            ]}
          />
        ),
        <Metric
          key={getMetricLabel('rejected_orders')}
          name={getMetricLabel('rejected_orders')}
          loading={isMetricLoading(['rejected_orders_amount', 'rejected_orders_count', 'rejected_orders_rate'])}
          mainMetrics={['rejected_orders_amount', 'rejected_orders_count', 'rejected_orders_rate']}
          excludedHistoryMetrics={['rejected_orders_rate_breakdown', 'rejected_orders_rate']}
          items={[
            {
              name: getMetricLabel('rejected_orders_amount'),
              formatter: moneyFormatter,
              ...stats.rejected_orders_amount,
            },
            {
              name: getMetricLabel('rejected_orders_count'),
              ...stats.rejected_orders_count,
            },
            {
              name: getMetricLabel('rejected_orders_rate'),
              formatter: percentageFormatter,
              ...stats.rejected_orders_rate,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('orders')}
          name={getMetricLabel('orders')}
          loading={isMetricLoading([
            'orders_count',
            'online_orders_count',
            'cash_orders_count',
            'pickup_orders_rate',
            'delivery_orders_rate',
            'other_orders_rate',
          ])}
          mainMetrics={[
            'orders_count',
            'online_orders_count',
            'cash_orders_count',
            'pickup_orders_rate',
            'delivery_orders_rate',
            'other_orders_rate',
          ]}
          excludedHistoryMetrics={['*']}
          items={[
            {
              name: getMetricLabel('total_orders'),
              ...stats.orders_count,
            },
            {
              name: getMetricLabel('online_orders_count'),
              ...stats.online_orders_count,
            },
            {
              name: getMetricLabel('cash_orders_count'),
              ...stats.cash_orders_count,
            },
            {
              name: getMetricLabel('pickup_orders_rate'),
              formatter: percentageFormatter,
              ...stats.pickup_orders_rate,
            },
            {
              name: getMetricLabel('delivery_orders_rate'),
              formatter: percentageFormatter,
              ...stats.delivery_orders_rate,
            },
            {
              name: getMetricLabel('other_orders_rate'),
              formatter: percentageFormatter,
              ...stats.other_orders_rate,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('reservations')}
          name={getMetricLabel('reservations')}
          loading={isMetricLoading(['total_reservations', 'website_reservations', 'google_reservations'])}
          reservationMetrics={['total_reservations', 'website_reservations', 'google_reservations']}
          items={[
            {
              name: getMetricLabel('total_reservations'),
              ...stats.total_reservations,
            },
            {
              name: getMetricLabel('website_reservations'),
              ...stats.website_reservations,
            },
            {
              name: getMetricLabel('google_reservations'),
              ...stats.google_reservations,
            },
          ]}
        />,
        franchise?.has_storefront && (
          <Metric
            key={getMetricLabel('storefront')}
            name={getMetricLabel('storefront')}
            loading={isMetricLoading(['website_visits', 'website_visitors'])}
            storefrontMetrics={['website_visits', 'website_visitors']}
            items={[
              {
                name: getMetricLabel('website_visits'),
                ...stats.website_visits,
              },
              {
                name: getMetricLabel('website_visitors'),
                ...stats.website_visitors,
              },
            ]}
          />
        ),
      ].filter(Boolean)
    : [];

  return <MetricsContainer metrics={metrics} />;
}

export default function General({ dateRangeFilter }) {
  const { company } = useSelector((state) => state.currentCompany);
  const { franchise } = company;

  return (
    <StatisticsProvider
      mainMetrics={[
        'sales',
        'cash_sales',
        'online_sales',
        'gift_cards_sales',
        'last_payout_amount',
        'pending_payout_amount',
        'future_payout_amount',
        'savings',
        'average_order_value',
        'tips',
        'pickup_orders_rate',
        'delivery_orders_rate',
        'other_orders_rate',
        'rejected_orders_amount',
        'rejected_orders_count',
        'rejected_orders_rate',
        'orders_count',
        'online_orders_count',
        'cash_orders_count',
      ]}
      storefrontMetrics={franchise?.has_storefront ? ['website_visits', 'website_visitors'] : []}
      marketingMetrics={['average_open_rate']}
      reservationMetrics={['total_reservations', 'website_reservations', 'google_reservations']}
      dateRangeFilter={dateRangeFilter}
    >
      <Metrics />
    </StatisticsProvider>
  );
}
